import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useCallState } from './CallProvider';

interface IPipContext {
	openPip: () => void;
	closePip: () => void;
	togglePip: () => void;
	isPipActive: boolean;
	isPipSupported: boolean;
}

const noop = () => { };

const PipContext = createContext<IPipContext>({
	openPip: noop,
	closePip: noop,
	togglePip: noop,
	isPipActive: false,
	isPipSupported: false,
});

const TILE_W = 640;
const TILE_H = 360;
const FPS = 15;
const LABEL_H = 64;

type TileKind = 'video' | 'screen';
type TileKey = string; // `${sessionId}::${TileKind}`

interface ITileSource {
	key: TileKey;
	kind: TileKind;
	label: string;
	video: HTMLVideoElement;
	track: MediaStreamTrack | null;
}

export const PipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { callObject } = useCallState();
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const offscreenHostRef = useRef<HTMLDivElement | null>(null);
	const tilesRef = useRef<Map<TileKey, ITileSource>>(new Map());
	const rafRef = useRef<number | null>(null);
	const [isPipActive, setIsPipActive] = useState(false);

	// Yandex Browser reports pictureInPictureEnabled=true but silently ignores
	// requestPictureInPicture() (the promise stays pending forever).
	const isBrokenPipBrowser = typeof navigator !== 'undefined'
		&& /YaBrowser|YaApp_/i.test(navigator.userAgent);

	const isPipSupported = typeof document !== 'undefined'
		&& !!document.pictureInPictureEnabled
		&& !isBrokenPipBrowser;

	const setStream = useCallback((stream: MediaStream | null) => {
		const v = videoRef.current;
		if (!v) return;
		if (v.srcObject === stream) return;
		v.srcObject = stream;
		if (stream) v.play().catch(() => { });
	}, []);

	const openPip = useCallback(() => {
		if (!isPipSupported) return;
		const v = videoRef.current;
		if (!v) return;
		if (document.pictureInPictureElement === v) return;

		const tryRequest = () => v.requestPictureInPicture().catch(() => { });

		if (v.readyState >= 1 /* HAVE_METADATA */) {
			tryRequest();
		} else {
			v.play().catch(() => { });
			v.addEventListener('loadedmetadata', tryRequest, { once: true });
		}
	}, [isPipSupported]);

	const closePip = useCallback(() => {
		if (document.pictureInPictureElement) {
			document.exitPictureInPicture().catch(() => { });
		}
	}, []);

	const togglePip = useCallback(() => {
		if (document.pictureInPictureElement) {
			closePip();
		} else {
			openPip();
		}
	}, [openPip, closePip]);

	useEffect(() => {
		const v = videoRef.current;
		if (!v) return;

		const onEnter = () => setIsPipActive(true);
		const onLeave = () => setIsPipActive(false);

		v.addEventListener('enterpictureinpicture', onEnter);
		v.addEventListener('leavepictureinpicture', onLeave);
		return () => {
			v.removeEventListener('enterpictureinpicture', onEnter);
			v.removeEventListener('leavepictureinpicture', onLeave);
		};
	}, []);

	// Composite all camera/screen tracks onto a canvas and feed the captured stream into the PiP <video>.
	useEffect(() => {
		if (!callObject) return;
		const canvas = canvasRef.current;
		const host = offscreenHostRef.current;
		if (!canvas || !host) return;

		canvas.width = TILE_W;
		canvas.height = TILE_H;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const resizeToTileCount = (k: number) => {
			const targetH = TILE_H * Math.max(k, 1);
			if (canvas.width !== TILE_W || canvas.height !== targetH) {
				canvas.width = TILE_W;
				canvas.height = targetH;
			}
			canvas.style.width = `${TILE_W}px`;
			canvas.style.height = `${targetH}px`;
			const v = videoRef.current;
			if (v) {
				v.style.width = `${TILE_W / 2}px`;
				v.style.height = `${targetH / 2}px`;
			}
		};

		const ensureTile = (key: TileKey, kind: TileKind, track: MediaStreamTrack | null, label: string) => {
			const existing = tilesRef.current.get(key);
			if (existing) {
				existing.label = label;
				if (existing.track !== track) {
					if (track) {
						const ms = new MediaStream();
						ms.addTrack(track);
						existing.video.srcObject = ms;
						existing.video.play().catch(() => { });
					} else {
						existing.video.srcObject = null;
					}
					existing.track = track;
				}
				return;
			}
			const videoEl = document.createElement('video');
			videoEl.autoplay = true;
			videoEl.playsInline = true;
			videoEl.muted = true;
			(videoEl as any).disablePictureInPicture = true;
			videoEl.style.position = 'absolute';
			videoEl.style.width = '1px';
			videoEl.style.height = '1px';
			videoEl.style.opacity = '0';
			videoEl.style.pointerEvents = 'none';
			host.appendChild(videoEl);
			if (track) {
				const ms = new MediaStream();
				ms.addTrack(track);
				videoEl.srcObject = ms;
				videoEl.play().catch(() => { });
			}
			tilesRef.current.set(key, { key, kind, label, video: videoEl, track });
		};

		const removeTile = (key: TileKey) => {
			const tile = tilesRef.current.get(key);
			if (!tile) return;
			tile.video.srcObject = null;
			tile.video.remove();
			tilesRef.current.delete(key);
		};

		const syncTiles = () => {
			const participants = callObject.participants() as Record<string, any>;
			const seen = new Set<TileKey>();

			Object.keys(participants).forEach((id) => {
				const p = participants[id];
				const sid = p?.session_id || id;
				const name = p?.user_name || sid || 'unknown';

				const vTrack = p?.tracks?.video?.state === 'playable'
					? p.tracks.video.persistentTrack : null;
				const vKey = `${sid}::video`;
				seen.add(vKey);
				ensureTile(vKey, 'video', vTrack, name);

				const sTrack = p?.tracks?.screenVideo?.state === 'playable'
					? p.tracks.screenVideo.persistentTrack : null;
				if (sTrack) {
					const sKey = `${sid}::screen`;
					seen.add(sKey);
					ensureTile(sKey, 'screen', sTrack, `${name} (screen)`);
				}
			});

			Array.from(tilesRef.current.keys()).forEach((key) => {
				if (!seen.has(key)) removeTile(key);
			});
		};

		const draw = () => {
			syncTiles();
			const tiles = Array.from(tilesRef.current.values());
			const k = tiles.length;
			resizeToTileCount(k);

			ctx.fillStyle = '#111';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			if (k === 0) return;

			tiles.forEach((tile, i) => {
				const x = 0;
				const y = i * TILE_H;
				const tileW = TILE_W;
				const tileH = TILE_H;

				ctx.fillStyle = '#000';
				ctx.fillRect(x, y, tileW, tileH);

				const v = tile.video;
				const hasVideo = !!tile.track
					&& v.readyState >= 2
					&& v.videoWidth > 0
					&& v.videoHeight > 0;

				if (!hasVideo) {
					const big = Math.max(16, Math.floor(tileH / 6));
					ctx.fillStyle = 'rgba(255,255,255,0.3)';
					ctx.font = `${big}px sans-serif`;
					ctx.textBaseline = 'middle';
					ctx.textAlign = 'center';
					ctx.fillText(tile.label, x + tileW / 2, y + tileH / 2);
					ctx.textAlign = 'start';
				}

				if (hasVideo) {
					const vAr = v.videoWidth / v.videoHeight;
					const tAr = tileW / tileH;
					let dx = x, dy = y, dw = tileW, dh = tileH;
					let sx = 0, sy = 0, sw = v.videoWidth, sh = v.videoHeight;

					if (tile.kind === 'screen') {
						// contain
						if (vAr > tAr) {
							dh = tileW / vAr;
							dy = y + (tileH - dh) / 2;
						} else {
							dw = tileH * vAr;
							dx = x + (tileW - dw) / 2;
						}
					} else {
						// cover
						if (vAr > tAr) {
							sw = v.videoHeight * tAr;
							sx = (v.videoWidth - sw) / 2;
						} else {
							sh = v.videoWidth / tAr;
							sy = (v.videoHeight - sh) / 2;
						}
					}

					try {
						ctx.drawImage(v, sx, sy, sw, sh, dx, dy, dw, dh);
					} catch { /* not ready yet */ }
				}

				ctx.font = (LABEL_H / 2) + 'px sans-serif';
				ctx.textBaseline = 'middle';
				const maxLen = Math.max(4, Math.floor(tileW / 6));
				const text = tile.label.length > maxLen
					? tile.label.slice(0, maxLen - 1) + '…'
					: tile.label;
				const tx = x + LABEL_H / 2;
				const ty = y + tileH - LABEL_H / 2;
				ctx.save();
				ctx.lineWidth = Math.max(2, LABEL_H / 12);
				ctx.lineJoin = 'round';
				ctx.miterLimit = 2;
				ctx.strokeStyle = 'rgba(0,0,0,0.85)';
				ctx.strokeText(text, tx, ty);
				ctx.fillStyle = '#fff';
				ctx.fillText(text, tx, ty);
				ctx.restore();
			});
		};

		// Prime the canvas before captureStream so the first frame isn't empty.
		draw();
		const stream = (canvas as any).captureStream(FPS) as MediaStream;
		setStream(stream);

		let lastDraw = performance.now();
		const interval = 1000 / FPS;
		const loop = (t: number) => {
			if (t - lastDraw >= interval) {
				draw();
				lastDraw = t;
			}
			rafRef.current = requestAnimationFrame(loop);
		};
		rafRef.current = requestAnimationFrame(loop);

		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
			tilesRef.current.forEach((t) => {
				t.video.srcObject = null;
				t.video.remove();
			});
			tilesRef.current.clear();
			stream.getTracks().forEach((t) => t.stop());
			setStream(null);
		};
	}, [callObject, setStream]);

	// Auto-open PiP when we start screen-sharing, close when nobody is sharing.
	// Viewers see the screen large in the tab, so for them PiP is manual only.
	useEffect(() => {
		if (!callObject) return;

		const handleTrackStarted = (ev: any) => {
			if (ev?.type !== 'screenVideo' || !ev?.participant?.local) return;
			openPip();
		};

		const handleTrackStopped = (ev: any) => {
			if (ev?.type !== 'screenVideo') return;
			setTimeout(() => {
				const participants = callObject.participants();
				const stillSharing = Object.values(participants).find(
					(p: any) => p?.tracks?.screenVideo?.state === 'playable'
				);
				if (!stillSharing) closePip();
			}, 1000);
		};

		callObject.on('track-started', handleTrackStarted);
		callObject.on('track-stopped', handleTrackStopped);

		return () => {
			callObject.off('track-started', handleTrackStarted);
			callObject.off('track-stopped', handleTrackStopped);
		};
	}, [callObject, openPip, closePip]);

	return (
		<PipContext.Provider
			value={{
				openPip,
				closePip,
				togglePip,
				isPipActive,
				isPipSupported,
			}}
		>
			{children}
			<div
				ref={offscreenHostRef}
				aria-hidden
				style={{
					position: 'fixed',
					left: 0,
					top: '100px',
					width: '100px',
					height: '100px',
					overflow: 'hidden',
					pointerEvents: 'none',
					zIndex: -1,
				}}
			/>
			<canvas
				ref={canvasRef}
				aria-hidden
				style={{
					position: 'fixed',
					left: '100px',
					top: '100px',
					width: TILE_W,
					height: TILE_H,
					overflow: 'hidden',
					pointerEvents: 'none',
					zIndex: -1,
				}}
			/>
			<video
				ref={videoRef}
				autoPlay
				playsInline
				muted
				style={{
					position: 'fixed',
					left: `${100 + TILE_W + 10}px`,
					top: '100px',
					width: TILE_W,
					height: TILE_H,
					pointerEvents: 'none',
					zIndex: -1,
				}}
			/>
		</PipContext.Provider>
	);
};

export const usePip = () => useContext(PipContext);
